import { z } from 'zod'

import { bpsSchema, isoDateSchema, moneyPaisa } from '../common'

/**
 * A22 · `GET /animals/:id/sale-quote` (§4.9).
 *
 * **The one payload a client is permitted to do arithmetic on.** As the trader
 * drags the price slider on screen 10 the app computes, live and locally:
 *
 *     commission   = round(price × sellCommissionBps / 10000)
 *     profit       = price − commission − expectedSaleCosts − costBasis
 *     profitPerDay = daysHeld > 0 ? profit / daysHeld : profit
 *     roi          = profit / costBasis × 100
 *
 * That is four operations on numbers the **server** chose, from a snapshot the
 * server issued — not a re-derivation of anything. A round trip per keystroke
 * is not viable over a mandi connection, and this is the whole of the
 * exception.
 *
 * **On confirm the client sends only the price** and `POST /animals/:id/sale`
 * recomputes everything from scratch; its response is the source of truth for
 * every figure shown afterwards. Cost basis, feed accrual, allocation and
 * break-even are never computed on a client, here or anywhere.
 *
 * This endpoint is money-only: a locked session gets `403 PRICE_LOCKED` and no
 * body at all, and a Staff account gets `403 FORBIDDEN`. The keys stay optional
 * regardless — the type must not tempt anyone into a non-null assertion.
 */
export const saleQuoteResponse = z.object({
  costBasisPaisa: moneyPaisa(),
  daysHeld: z.number().int().nonnegative(),
  sellCommissionBps: bpsSchema,
  /** The tenant's fixed expected sale costs — transport, mandi fee, labour. */
  expectedSaleCostsPaisa: moneyPaisa(),
  /** `ceil((cost + fixed) / (1 − c))`. Solved for, never `cost + c × cost`. */
  breakEvenPaisa: moneyPaisa(),
  dailyBurnPaisa: moneyPaisa(),
  /** The snapshot's date. A quote read yesterday is stale by one day of feed. */
  asOf: isoDateSchema,
  /**
   * §4.8 · "Hold him another week and you need Rs 2,64,433." Break-even after
   * N more days is a *break-even*, so the server computes each one; the client
   * renders the row it was given rather than extrapolating the daily burn.
   */
  projections: z
    .array(
      z.object({
        extraDays: z.number().int().positive(),
        breakEvenPaisa: moneyPaisa(),
      }),
    )
    .optional(),
  /** What he is currently asking, if anything, so the slider can start there. */
  askingPricePaisa: moneyPaisa(),
})

export type SaleQuoteResponse = z.infer<typeof saleQuoteResponse>
